import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { MealsPage } from "./MealsPage";
import { PhotosPage } from "./PhotosPage";
import { ProgressPage } from "./ProgressPage";
import { SettingsHubPage } from "./SettingsHubPage";
import type {
  AppPage,
  AppSettings,
  DashboardSummary,
  SessionState,
} from "./types";

const pageLabels: Record<AppPage, string> = {
  today: "今日",
  meals: "食事",
  progress: "変化",
  photos: "写真",
  settings: "設定",
};

function LoginView({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      onLoggedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "ログインできませんでした。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="app-mark" aria-hidden="true">◯</div>
        <p className="eyebrow">PRIVATE HEALTH LOG</p>
        <h1>ダイエット記録</h1>
        <p className="muted">
          完璧に埋めなくても大丈夫。今日できた記録から、また始めます。
        </p>
        <form onSubmit={submit} className="stack">
          <label className="field">
            <span>パスワード</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button className="button primary" disabled={submitting}>
            {submitting ? "確認中…" : "ログイン"}
          </button>
        </form>
        {error && <p className="form-error">{error}</p>}
      </section>
    </main>
  );
}

function MetricCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note: string;
}) {
  return (
    <article className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function TodayPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ ok: true; summary: DashboardSummary }>("/api/app/dashboard")
      .then((data) => setSummary(data.summary))
      .catch((err) => setError(err instanceof Error ? err.message : "読み込めませんでした。"));
  }, []);

  if (error) return <div className="notice error">{error}</div>;
  if (!summary) return <div className="notice">今日の記録を読み込んでいます…</div>;

  const pfc = `${Math.round(summary.proteinG)}g / ${Math.round(summary.fatG)}g / ${Math.round(summary.carbsG)}g`;
  const weight = summary.latestWeightKg === null ? "未記録" : `${summary.latestWeightKg.toFixed(1)}kg`;

  return (
    <>
      <section className="hero-card">
        <p className="eyebrow">TODAY</p>
        <div className="hero-heading">
          <div>
            <h2>一件だけでも、今日は前進</h2>
            <p>空いていた日の穴埋めはしなくて大丈夫です。</p>
          </div>
          <div className="record-count">
            <strong>{summary.mealCount + summary.activityCount}</strong>
            <span>件記録</span>
          </div>
        </div>
      </section>

      <section className="metric-grid" aria-label="今日の集計">
        <MetricCard label="摂取カロリー" value={`${Math.round(summary.caloriesKcal)} kcal`} note="写真推定を含みます" />
        <MetricCard label="P / F / C" value={pfc} note="たんぱく質・脂質・炭水化物" />
        <MetricCard label="最新体重" value={weight} note={summary.targetWeightKg ? `目標 ${summary.targetWeightKg}kg` : "目標は未設定"} />
        <MetricCard label="運動" value={`${summary.activityCount}件`} note="短い運動も記録対象です" />
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">QUICK START</p>
            <h3>専用GPTから記録</h3>
          </div>
          <span className="status-chip">準備中</span>
        </div>
        <p>
          食事写真、体重、運動報告は専用GPTから登録します。アクション接続後、ここに最新記録が自動で反映されます。
        </p>
      </section>
    </>
  );
}

function SettingsPage() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    api<{ ok: true; settings: AppSettings }>("/api/app/settings")
      .then((data) => setSettings(data.settings))
      .catch((err) => setMessage(err instanceof Error ? err.message : "設定を取得できませんでした。"));
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setMessage("保存中…");
    try {
      const result = await api<{ ok: true; settings: AppSettings }>("/api/app/settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSettings(result.settings);
      setMessage("保存しました。");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "保存できませんでした。");
    }
  }

  if (!settings) return <div className="notice">{message || "設定を読み込んでいます…"}</div>;

  const updateNumber = (key: keyof AppSettings, value: string) => {
    setSettings((current) =>
      current ? { ...current, [key]: value === "" ? null : Number(value) } : current,
    );
  };

  return (
    <form className="panel settings-form" onSubmit={save}>
      <div className="panel-heading">
        <div>
          <p className="eyebrow">GOALS</p>
          <h2>目標設定</h2>
        </div>
        <span className="status-chip">後から変更可能</span>
      </div>
      <div className="form-grid">
        <label className="field">
          <span>アプリ名</span>
          <input
            value={settings.appName ?? ""}
            placeholder="未設定"
            onChange={(event) => setSettings({ ...settings, appName: event.target.value || null })}
          />
        </label>
        <label className="field">
          <span>目標体重（kg）</span>
          <input
            type="number"
            step="0.1"
            value={settings.targetWeightKg ?? ""}
            placeholder="未設定"
            onChange={(event) => updateNumber("targetWeightKg", event.target.value)}
          />
        </label>
        <label className="field">
          <span>一日カロリー（kcal）</span>
          <input
            type="number"
            value={settings.targetCaloriesKcal ?? ""}
            placeholder="未設定"
            onChange={(event) => updateNumber("targetCaloriesKcal", event.target.value)}
          />
        </label>
        <label className="field">
          <span>たんぱく質（g）</span>
          <input
            type="number"
            step="0.1"
            value={settings.targetProteinG ?? ""}
            placeholder="未設定"
            onChange={(event) => updateNumber("targetProteinG", event.target.value)}
          />
        </label>
        <label className="field">
          <span>脂質（g）</span>
          <input
            type="number"
            step="0.1"
            value={settings.targetFatG ?? ""}
            placeholder="未設定"
            onChange={(event) => updateNumber("targetFatG", event.target.value)}
          />
        </label>
        <label className="field">
          <span>炭水化物（g）</span>
          <input
            type="number"
            step="0.1"
            value={settings.targetCarbsG ?? ""}
            placeholder="未設定"
            onChange={(event) => updateNumber("targetCarbsG", event.target.value)}
          />
        </label>
        <label className="field field-wide">
          <span>アドバイスの強さ</span>
          <select
            value={settings.adviceMode}
            onChange={(event) =>
              setSettings({ ...settings, adviceMode: event.target.value as AppSettings["adviceMode"] })
            }
          >
            <option value="record_only">記録のみ</option>
            <option value="gentle">やさしい助言</option>
            <option value="detailed">しっかり分析</option>
          </select>
        </label>
      </div>
      <button className="button primary">設定を保存</button>
      {message && <p className="form-message">{message}</p>}
    </form>
  );
}

function AppShell({ onLogout }: { onLogout: () => void }) {
  const [page, setPage] = useState<AppPage>("today");

  const content = useMemo(() => {
    if (page === "today") return <TodayPage />;
    if (page === "meals") return <MealsPage />;
    if (page === "progress") return <ProgressPage />;
    if (page === "photos") return <PhotosPage />;
    return <SettingsHubPage />;
  }, [page]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">PRIVATE DIET LOG</p>
          <h1>{pageLabels[page]}</h1>
        </div>
        <button className="icon-button" onClick={onLogout} aria-label="ログアウト">↪</button>
      </header>

      <main className="content">{content}</main>

      <nav className="bottom-nav" aria-label="メインメニュー">
        {(Object.keys(pageLabels) as AppPage[]).map((key) => (
          <button
            key={key}
            className={page === key ? "active" : ""}
            onClick={() => setPage(key)}
          >
            <span aria-hidden="true">
              {key === "today" ? "⌂" : key === "meals" ? "◒" : key === "progress" ? "↗" : key === "photos" ? "▣" : "⚙"}
            </span>
            <small>{pageLabels[key]}</small>
          </button>
        ))}
      </nav>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<SessionState | null>(null);

  const checkSession = useCallback(() => {
    api<SessionState>("/api/auth/session")
      .then(setSession)
      .catch(() => setSession({ authenticated: false }));
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  async function logout() {
    await api("/api/auth/logout", { method: "POST" });
    setSession({ authenticated: false });
  }

  if (!session) return <main className="splash">準備しています…</main>;
  if (!session.authenticated) return <LoginView onLoggedIn={checkSession} />;
  return <AppShell onLogout={logout} />;
}
